"use client";

import { Sparkles, Trash2 } from "lucide-react";
import React, { type RefObject } from "react";
import { MediaPreview } from "@/components/transcriptions/MediaPreview";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { timeMsToSrt } from "@/lib/subs/srt";
import { UploadProgress } from "@/lib/upload/UploadProgress";
import type { Uploader } from "./multipart-upload";

type UploadStatus = "INITIAL" | "LOADING" | "ERROR" | "SUCCESS";

type SubmitButtonProps = {
  label: string;
  canSubmit: boolean;
  isLoading: boolean;
  submitButtonRef?: RefObject<HTMLButtonElement | null>;
};

export const BeginTranscriptionButton = ({
  label,
  canSubmit,
  isLoading,
  submitButtonRef,
}: SubmitButtonProps) => (
  <Button
    ref={submitButtonRef}
    className="w-full text-lg sm:w-auto"
    size="lg"
    type="submit"
    disabled={!canSubmit}
    loading={isLoading}
    form="start-transcription-form"
  >
    {label} <Sparkles className="ml-2" />
  </Button>
);

export const SelectedMediaPreviewPanel = ({
  url,
  mediaName,
  duration,
  sizeMb,
  type,
  mediaPreviewRef,
  resetFormFile,
  canSubmit,
  isLoading,
  uploader,
  uploadStatus,
  submitLabel,
  removeLabel,
  submitButtonRef,
}: {
  url: string | undefined;
  mediaName: string | undefined;
  duration: number;
  sizeMb: number;
  type: string;
  mediaPreviewRef: RefObject<HTMLVideoElement | HTMLAudioElement | null>;
  resetFormFile: () => void;
  canSubmit: boolean;
  isLoading: boolean;
  uploader: RefObject<Uploader | null>;
  uploadStatus: UploadStatus;
  submitLabel: string;
  removeLabel: string;
  submitButtonRef?: RefObject<HTMLButtonElement | null>;
}) => {
  if (!url) return null;

  return (
    <div className="shadow-sm rounded-xl px-4 py-4 bg-linear-to-r from-info-surface-from/20 to-info-surface-to/20 mt-4 sm:px-8">
      <div className="grid gap-y-4 md:grid-cols-2">
        <div className="min-w-0 space-y-2">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 text-foreground/80">
              <div className="break-all">{mediaName || "No file"}</div>
              <div className="text-sm">
                <span>{timeMsToSrt({ timeMs: duration * 1000, keepMinutes: true })}</span>
                &nbsp;
                <span>{sizeMb.toFixed(0)}MB</span>
              </div>
            </div>
            <Button
              title={removeLabel}
              variant="destructive"
              size="icon"
              type="button"
              onClick={resetFormFile}
            >
              <Trash2 size={16} />
            </Button>
          </div>
          {/* Hidden (not unmounted) on mobile: the element still loads
              metadata for the duration/credit pre-check, but the player
              pushed the submit button below the fold (Viktor 2026-07-03). */}
          <div className="hidden md:block">
            <MediaPreview url={url} type={type} ref={mediaPreviewRef} />
          </div>
        </div>
        <div className="flex items-center justify-center">
          <BeginTranscriptionButton
            label={submitLabel}
            canSubmit={canSubmit}
            isLoading={isLoading}
            submitButtonRef={submitButtonRef}
          />
        </div>
      </div>
      <div className="mt-2">
        <UploadProgress uploader={uploader} uploadStatus={uploadStatus} />
      </div>
    </div>
  );
};

export const UrlInfoPreviewPanel = ({
  urlInfoLoading,
  urlInfo,
  canSubmit,
  isLoading,
  submitLabel,
  submitButtonRef,
}: {
  urlInfoLoading: boolean;
  urlInfo?: UrlInfo;
  canSubmit: boolean;
  isLoading: boolean;
  submitLabel: string;
  submitButtonRef?: RefObject<HTMLButtonElement | null>;
}) => {
  if (!(urlInfoLoading || urlInfo)) return null;

  return (
    <div className="mx-auto border rounded-2xl px-4 py-4 bg-linear-to-r from-info-surface-from/20 to-info-surface-to/20 text-start sm:px-8">
      {urlInfoLoading ? (
        <div className="flex items-center justify-center">
          <Spinner className="size-12 text-foreground/60" />
        </div>
      ) : urlInfo?.type === "youtube" ? (
        <div className="grid gap-4 md:grid-cols-2">
          <div className="min-w-0">
            <div className="break-words">
              {urlInfo.title} (
              {timeMsToSrt({
                timeMs: (urlInfo.duration_s ?? 0) * 1000,
                keepMinutes: true,
              })}
              )
            </div>
            <img
              src={urlInfo.thumbnail}
              alt={urlInfo.title}
              className="object-center object-cover max-w-32 max-h-32"
            />
          </div>
          <div className="flex items-center justify-center">
            <BeginTranscriptionButton
              label={submitLabel}
              canSubmit={canSubmit}
              isLoading={isLoading}
              submitButtonRef={submitButtonRef}
            />
          </div>
        </div>
      ) : urlInfo ? (
        <div className="grid gap-4 md:grid-cols-2">
          <div className="min-w-0 break-words">
            {urlInfo.title} ({urlInfo.sizeMb}MB)
          </div>
          <div className="flex items-center justify-center">
            <BeginTranscriptionButton
              label={submitLabel}
              canSubmit={canSubmit}
              isLoading={isLoading}
              submitButtonRef={submitButtonRef}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};
